import { useEffect, useState } from 'react'

import { NEW_LINE } from '~/constants'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useGetAgentEmailSignature from '~/features/agents/hooks/useGetAgentEmailSignature'
import useGetAgentPhoneSettings from '~/features/agents/hooks/useGetAgentPhoneSettings'
import useGetOrPostChatbotSettings from '~/features/agents/hooks/useGetOrPostChatbotSettings'
import { getStringFromAgentFacts } from '~/features/agents/utils'
import { voiceOptions } from '~/features/settings/components/phoneText/phoneTextForm/utils'
import { DEFAULT_PHONE_PATIENCE } from '~/features/settings/constants'
import {
  EAgentLanguage,
  EBasicInfoKeys,
  EChatbotDetails,
  EConvoOptionsKeys,
  EEmail,
  EEmailStatus,
  EKnowledgeV1Keys,
  ELoopingAudio,
  ENotificationsKeys,
  EPhoneStatus,
  EPhoneTextKeys,
  ESettingsStepFriendlyNames,
  ETextStatus,
} from '~/features/settings/models'
import {
  EAgentNotifyWhen,
  ECTAValues,
  ELocationTypeValues,
  ENotificationType,
  EPersonalityFriendlyName,
  EScheduleValues,
  ETextMethodValues,
  personalityMapping,
} from '~/models/features'
import { sessionStore, store } from '~/store'

// todo: throw out settings in favor of centralized agent data. Then remove this file.
export default function useSetSettingsSteps(): boolean {
  const [settings, setSettings] = store((state) => [
    state.settings,
    state.setSettings,
  ])

  const agentId = sessionStore((state) => state.agent.id) || ''

  const {
    selectedAgent: agent,
    isSuccess,
    isLoading: isLoadingAgent,
  } = useGetAgentCentralized()

  const {
    data: agentSignature,
    isSuccess: isSuccessAgentEmailSignature,
    isLoading: isLoadingSignature,
  } = useGetAgentEmailSignature(agentId)

  const {
    data: agentPhoneSettings,
    isSuccess: isSuccessAgentPhoneSettings,
    isLoading: isLoadingPhoneSettings,
  } = useGetAgentPhoneSettings(agentId)

  const isSettingsAlreadySet = !!Object.entries(settings?.steps || {}).some(
    ([key, value]) => key !== ESettingsStepFriendlyNames.chatbot && !!value,
  )
  const [isLoadingAgentData, setIsLoadingAgentData] =
    useState<boolean>(!isSettingsAlreadySet)
  const layoutData = useGetOrPostChatbotSettings()

  useEffect(() => {
    if (
      !isLoadingAgent &&
      !isLoadingSignature &&
      !isLoadingPhoneSettings &&
      isSuccess &&
      isSuccessAgentEmailSignature &&
      isSuccessAgentPhoneSettings
    ) {
      const updatedBasicInfoData = {
        [EBasicInfoKeys.businessName]: agent?.businessName || '',
        [EBasicInfoKeys.service]: agent?.service || '',
        [EBasicInfoKeys.siteLink]: agent?.siteLink || '',
      }
      const personality: EPersonalityFriendlyName =
        EPersonalityFriendlyName.professional

      const updatedConvoOptionsData = {
        [EConvoOptionsKeys.agentName]: agent?.agentName || '',
        [EConvoOptionsKeys.cta]: agent?.cta || ECTAValues.schedule,
        [EConvoOptionsKeys.calendlyLink]: agent?.calendlyLink || '',
        [EConvoOptionsKeys.locationType]:
          agent?.locationType || ELocationTypeValues.anywhere,

        [EConvoOptionsKeys.ctaText]: agent?.ctaText || null,
        [EConvoOptionsKeys.scheduleType]:
          agent?.scheduleType || EScheduleValues.askForAvailability,

        [EConvoOptionsKeys.location]: agent?.location || '',
        [EConvoOptionsKeys.questions]: agent?.questions || [],
        [EConvoOptionsKeys.pricing]: agent?.pricing || '',
        [EConvoOptionsKeys.personality]:
          agent?.personality || personalityMapping[personality],
        [EConvoOptionsKeys.customLinks]: agent?.customLinks || [],
        [EConvoOptionsKeys.customInstructions]: agent?.customInstructions || [
          {},
        ],
      }

      const updatedNotificationsData = {
        [ENotificationsKeys.notifyType]:
          agent?.notifyType || ENotificationType.email,
        [ENotificationsKeys.notifyEmail]: agent?.notifyEmail || '',
        [ENotificationsKeys.notifyWhen]:
          agent?.notifyWhen || EAgentNotifyWhen.afterAllChats,
        [ENotificationsKeys.notifyMobile]: agent?.notifyMobile || '',
        [ENotificationsKeys.mobileConsent]: agent?.mobileConsent || false,
        [ENotificationsKeys.zapierId]: agent?.zapierId || '',
        [ENotificationsKeys.textMethod]:
          agent?.textMethod || ETextMethodValues.SMS,
      }

      const defaultPhoneIntro = `Hi!  I'm the AI assistant for ${agent?.businessName}.  This call is being recorded and you can say voicemail if you want to leave one. How can I help you?`
      const updatedPhoneTextData = {
        [EPhoneTextKeys.phoneStatus]:
          EPhoneStatus[agent?.phoneStatus as keyof typeof EPhoneStatus] ||
          EPhoneStatus.active,

        [EPhoneTextKeys.phoneVoice]: agent?.phoneVoice || voiceOptions[0].id,
        [EPhoneTextKeys.phoneNumber]: agent?.phoneNumber || '',
        [EPhoneTextKeys.phoneIntro]: agent?.phoneIntro || defaultPhoneIntro,
        [EPhoneTextKeys.phoneIntroRecording]: agent?.phoneIntroRecording || '',
        [EPhoneTextKeys.agentLanguage]:
          agent?.agentLanguage || EAgentLanguage.English,
        [EPhoneTextKeys.textStatus]: agent?.textStatus || ETextStatus.disabled,
        [EPhoneTextKeys.loopingAudio]: agent?.loopingAudio || ELoopingAudio.off,
        [EPhoneTextKeys.patience]:
          agentPhoneSettings.data?.patience || DEFAULT_PHONE_PATIENCE,
        [EPhoneTextKeys.phoneRules]:
          agentPhoneSettings.data?.phoneRules?.join(NEW_LINE) || '',
        [EPhoneTextKeys.backupNumber]:
          agentPhoneSettings.data?.backupNumber || '',
        [EPhoneTextKeys.outageResponse]:
          agentPhoneSettings.data?.outageResponse || '',
        [EPhoneTextKeys.textFollowUpClosing]:
          agentPhoneSettings.data?.textFollowUpClosing || '',
      }

      const updatedEmailData = {
        [EEmail.emailStatus]:
          (agent?.emailStatus as EEmailStatus) || EEmailStatus.active,
        [EEmail.agentEmail]: agent?.agentEmail || '',
        [EEmail.signature]: agentSignature?.data?.signature || '',
        [EEmail.isDefaultSignature]:
          agentSignature?.data?.isDefaultSignature ?? true,
      }

      const updatedChatbotDetails = {
        [EChatbotDetails.chatIntro]: agent?.chatIntro || '',
        [EChatbotDetails.chatbotImgURL]: agent?.chatbotImgURL || null,
        [EChatbotDetails.showLibby]: agent?.showLibby,
        [EChatbotDetails.showBranding]: agent?.showBranding,
        [EChatbotDetails.chatFriendlyUrl]: agent?.chatFriendlyUrl || '',
      }

      const updatedKnowledgeData = {
        [EKnowledgeV1Keys.facts]: getStringFromAgentFacts(agent?.facts),
      }

      setSettings({
        steps: {
          [ESettingsStepFriendlyNames.basicInfo]: updatedBasicInfoData,
          [ESettingsStepFriendlyNames.convoOptions]: updatedConvoOptionsData,
          [ESettingsStepFriendlyNames.knowledge]: updatedKnowledgeData,
          [ESettingsStepFriendlyNames.notifications]: updatedNotificationsData,
          [ESettingsStepFriendlyNames.chatbot]: {
            layout: layoutData!,
            details: updatedChatbotDetails,
          },
          [ESettingsStepFriendlyNames.email]: updatedEmailData,
          [ESettingsStepFriendlyNames.phoneText]: updatedPhoneTextData,
        },
      })

      setIsLoadingAgentData(false)
    }
  }, [
    agent,
    isSuccess,
    isSuccessAgentEmailSignature,
    isSuccessAgentPhoneSettings,
    agentSignature,
    agentPhoneSettings,
    layoutData,
    isLoadingAgent,
    isLoadingSignature,
    isLoadingPhoneSettings,
    setSettings,
  ])

  return isLoadingAgentData && !!agentId && !isLoadingAgent
}
